import { createContext, useContext, useState, useEffect } from 'react';
import { organizationAPI, teamAPI } from '@/services/api';
import { useAuth } from './AuthContext';

const OrgContext = createContext(null);

function safeGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeSet(key, value) {
  try { localStorage.setItem(key, value); } catch {}
}
function safeRemove(key) {
  try { localStorage.removeItem(key); } catch {}
}

export function OrgProvider({ children }) {
  const { user } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [memberRole, setMemberRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      setOrganization(null);
      setMemberRole(null);
      setLoading(false);
      setError(null);
      return;
    }

    const loadOrgData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current organization
        const orgRes = await organizationAPI.getCurrent();
        const org = orgRes.data;
        setOrganization(org);

        // Get team data to determine user's role
        try {
          const teamRes = await teamAPI.getCurrent();
          const team = teamRes.data;

          // Find user's role in the team
          const member = team?.members?.find(m => m.user?._id === user._id || m._id === user._id);
          if (member) {
            setMemberRole(member.role);
          } else if (team?.owner?._id === user._id) {
            setMemberRole('owner');
          } else {
            setMemberRole('viewer'); // Default fallback
          }
        } catch (teamErr) {
          console.error('Failed to load team data:', teamErr);
          setMemberRole('owner'); // Assume owner if no team
        }

        // Cache org data
        safeSet('organization', JSON.stringify(org));
        safeSet('memberRole', memberRole || 'owner');
      } catch (err) {
        console.error('Failed to load organization data:', err);
        setError(err.response?.data?.message || 'Failed to load organization data');
        setOrganization(null);
        setMemberRole(null);
        safeRemove('organization');
        safeRemove('memberRole');
      } finally {
        setLoading(false);
      }
    };

    loadOrgData();
  }, [user]);

  const refreshOrg = async () => {
    if (!user) return;
    try {
      const orgRes = await organizationAPI.getCurrent();
      setOrganization(orgRes.data);
      safeSet('organization', JSON.stringify(orgRes.data));
    } catch (err) {
      console.error('Failed to refresh organization:', err);
    }
  };

  const refreshRole = async () => {
    if (!user) return;
    try {
      const teamRes = await teamAPI.getCurrent();
      const team = teamRes.data;
      const member = team?.members?.find(m => m.user?._id === user._id || m._id === user._id);
      if (member) {
        setMemberRole(member.role);
        safeSet('memberRole', member.role);
      } else if (team?.owner?._id === user._id) {
        setMemberRole('owner');
        safeSet('memberRole', 'owner');
      }
    } catch (err) {
      console.error('Failed to refresh role:', err);
    }
  };

  // Permission helpers
  const can = (action) => {
    if (!memberRole) return false;
    
    const permissions = {
      owner: ['view', 'edit', 'delete', 'manage_members', 'manage_settings', 'manage_billing', 'manage_integrations'],
      admin: ['view', 'edit', 'delete', 'manage_members', 'manage_settings', 'manage_integrations'],
      member: ['view', 'edit'],
      viewer: ['view']
    };

    return permissions[memberRole]?.includes(action) || false;
  };

  const isOwner = memberRole === 'owner';
  const isAdmin = memberRole === 'admin' || memberRole === 'owner';
  const isMember = memberRole === 'member' || isAdmin;
  const isViewer = memberRole === 'viewer' || isMember;

  return (
    <OrgContext.Provider value={{
      organization,
      memberRole,
      loading,
      error,
      refreshOrg,
      refreshRole,
      can,
      isOwner,
      isAdmin,
      isMember,
      isViewer
    }}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
}
